import Mustache from 'mustache';

class SiteSearch {
  constructor(form) {
    this.searchType = form.dataset.searchType;
    this.elements = this.getElements(form);
    this.templates = this.getSearchResultElements('search-results-list-template');
    this.language = document.documentElement.lang;
    this.url = window.location.origin + window.location.pathname;
    this.keyUpTimer;

    this.addEventListeners();
  }

  getElements(form) {
    let searchTypeSpecificElements = {};

    if (this.searchType === 'full-search') {
      searchTypeSpecificElements = {
        searchTermHeading: document.getElementById('site-search-term'),
        progressBar: document.getElementById('site-search-progress-bar'),
        searchResults: document.getElementById('site-search-results')
      };
    }
    else if (this.searchType === 'quick-search') {
      searchTypeSpecificElements = {
        navSearchContainer: document.getElementById('nav-site-search'),
        navSearchInput: document.getElementById('nav-site-search-input'),
        navSearchOpenButton: document.getElementById('nav-site-search-button'),
        navSearchInputContainer: document.getElementById('nav-site-search-input-container'),
        navSearchCloseButton: document.getElementById('nav-site-search-close'),
        navItems: document.getElementsByClassName('navigation-item'),
        progressBar: document.getElementById('nav-site-search-progress-bar'),
        searchResults: document.getElementById('nav-site-search-results')
      };
    }

    return {
      ...searchTypeSpecificElements,
      searchForm: form,
      searchResultLists: this.getSearchResultElements('search-results-list'),
      mainNavigation: document.getElementById('main-navigation')
    };
  }

  getSearchResultElements(className) {
    const searchResultElements = document.getElementsByClassName(className);

    return searchResultElements.length ? Array.from(searchResultElements)
      .filter(element => element.dataset.searchType === this.searchType)
      .reduce((elements, element) => ({
        ...elements,
        [element.dataset.resultType]: element
      }), {}) : [];
  }

  addEventListeners() {
    if (this.searchType === 'full-search') {
      this.elements.searchForm.addEventListener('submit', event => this.onSearchFormSubmit(event));
    }
    else if (this.searchType === 'quick-search') {
      this.elements.navSearchInput.addEventListener('input', event => this.onSearchInput(event.target.value));
      this.elements.navSearchCloseButton.addEventListener('click', () => this.hideNavSiteSearch());

      this.elements.navSearchInput.addEventListener('keydown', ({ key }) => {
        if (key === 'Escape' || key === 'Esc') {
          this.hideNavSiteSearch();
        }
      });

      document.addEventListener('click', ({ target }) => {
        if (!this.elements.navSearchInputContainer.hidden && !this.elements.navSearchContainer.contains(target)) {
          this.hideNavSiteSearch();
        }
      });
    }
  }

  onSearchFormSubmit(event, searchKey = 'search') {
    event.preventDefault();
    this.typingTimer && clearTimeout(this.typingTimer);

    const searchTerm = event.target.elements.search.value.trim(),
      queryParams = searchTerm ? `?${searchKey}=${searchTerm}` : '';

    // Update heading text
    this.elements.searchTermHeading.textContent = searchTerm;

    // Update url
    window.history.pushState(null, null, this.url + queryParams);

    this.toggleProgressBarVisibility(true);
    this.executeSearch(searchTerm);
  }

  onSearchInput(searchValue, keyUpSearchDelay = 500) {
    this.typingTimer && clearTimeout(this.typingTimer);

    this.typingTimer = setTimeout(() => {
      document.body.classList.add('no-scroll');
      this.elements.searchResults.hidden = false;
      this.toggleProgressBarVisibility(true);
      this.executeSearch(searchValue.trim());
    }, keyUpSearchDelay);
  }

  toggleProgressBarVisibility(shouldShow = true) {
    if (shouldShow) {
      this.elements.searchResults.classList.add('loading');
      this.elements.progressBar.classList.add('animate');
    }
    else {
      this.elements.searchResults.classList.remove('loading');
      this.elements.progressBar.classList.remove('animate');
    }
  }

  hideNavSiteSearch() {
    this.elements.navSearchInputContainer.hidden = true;
    this.elements.searchResults.hidden = true;
    this.elements.navSearchOpenButton.classList.remove('site-search-open');
    this.typingTimer && clearTimeout(this.typingTimer);

    document.body.classList.remove('no-scroll');

    Array.from(this.elements.navItems).forEach(item => item.style.pointerEvents = '');
  }

  executeSearch(searchValue, ajaxFunctionName = 'get-search-results', searchKey = 'search') {
    const searchUrl = `/stage-ajax/${ajaxFunctionName}`,
      queryParams = `?${searchKey}=${searchValue}&type=${this.searchType}&stage-language=${this.language}`;

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', searchUrl + queryParams);
      xhr.onload = resolve;
      xhr.onerror = reject;
      xhr.send();
    })
      .then(({ target }) => {
        return JSON.parse(target.response);
      })
      .then(searchResults => {
        this.toggleProgressBarVisibility(false);
        this.renderSearchResults(searchResults);
      });
  }

  renderSearchResults(searchResults) {
    if (this.searchType === 'quick-search') {
      // If car and/or personnel results found, show only the section with more results
      if (searchResults.hits.cars.length > searchResults.hits.personnel.length) {
        this.elements.searchResultLists.cars.parentElement.hidden = false;
        this.elements.searchResultLists.personnel.parentElement.hidden = false;
      }
      else if (searchResults.hits.cars.length < searchResults.hits.personnel.length) {
        this.elements.searchResultLists.cars.parentElement.hidden = true;
        this.elements.searchResultLists.personnel.parentElement.hidden = false;
      }
      else {
        this.elements.searchResultLists.cars.parentElement.hidden = false;
        this.elements.searchResultLists.personnel.parentElement.hidden = false;
      }
    }

    Object.entries(searchResults.hits).forEach(([resultType, results]) => {
      this.elements.searchResultLists[resultType].innerHTML = Mustache.render(this.templates[resultType].innerHTML, {
        [resultType]: results,
        siteSearchUrl: searchResults.siteSearchUrl
      });

      // Sort sections by results count
      if (this.searchType === 'full-search') {
        this.elements.searchResultLists[resultType].parentElement.style.order = -(results.length);
      }
    });
  }
}

function initSiteSearch(className = 'site-search-form') {
  const siteSearchForms = document.getElementsByClassName(className);

  if (siteSearchForms.length) {
    Array.from(siteSearchForms).forEach(form => new SiteSearch(form));
  }
}

export default initSiteSearch;
